import { Component, OnInit, ViewChild, OnDestroy, Input, ElementRef } from '@angular/core';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EventService } from '@shared/event.service';
import { DynamicFormService } from '@shared/components/dynamic-form/dynamic-form.service';
import { ModalDialogComponent } from '@shared/components/modal-dialog/modal-dialog.component';
import { ROLE_TYPE } from '@user/role-type';
import { UserService } from '@user/user.service';
import { TagType } from '@shared/components/files/shared/models/tag-type.model';
import { FilesActions } from '@shared/components/files/shared/models/action.model';
import { DOCUMENT_TAG_TYPE } from '@shared/components/files/shared/document-tag-type.enum';
import { ProgramFormService, getFormFilesList, FORM_ID } from '../../../shared/program-form.service';
import { ProgramApplication } from '../../../shared/program-application.model';
import { Program, PROGRAM_TYPE } from '../../../shared/program.model';
import { ProgramApplicationService } from '../../../shared/program-application.service';
import { ProgramService } from '../../../shared/program.service';
import { ProgramPageStatusService } from '../../../shared/program-page-status.service';
import { ProgramApplicationFormService } from '../../../shared/program-application-form.service';
import { ProgramModulePermissions } from '@program/shared/program-module-permissions.model';
import { FORM_TYPE } from '@program/shared/formio-custom-components/form-type.enum';
import { DocumentDataService } from '@shared/components/files/shared/document-data.service';
import { SideNavigationSecondaryService } from 'src/app/side-navigation-secondary/side-navigation-secondary.service';
import { UtilityService } from '@shared/services/utility.service';
import { DynamicFormComponent } from '@shared/components/dynamic-form/dynamic-form.component';

@Component({
  selector: 'oes-application-developer',
  templateUrl: './application-developer.component.html',
  styleUrls: ['./application-developer.component.scss'],
  providers: [
    DynamicFormService,
    ProgramFormService
  ]
})
export class ProgramApplicationDeveloperComponent implements OnInit, OnDestroy {
  @Input() stage: number;
  @Input() displayWarning: boolean = true;
  @Input() subHeading: string;

  @ViewChild(ModalDialogComponent, {static: false}) modalDialogComponent: ModalDialogComponent;
  @ViewChild('oesDynamicForm') set oesDynamicForm(comp: DynamicFormComponent) {
    if (comp) {
      this.oesDynamicFormComponent = comp;
    }
  };

  currentRole: ROLE_TYPE;
  currentStatusSubmitted: boolean;
  dataRoomReadOnly: boolean;
  formValid: boolean = false;
  json: any;
  loadingForm: boolean = true;
  program: Program;
  programApplication: ProgramApplication;
  readOnly: boolean;
  roleType = ROLE_TYPE;
  tabs = {
    form: true,
    attachments: false,
  };
  tagTypes: TagType[];
  takeUserBackUrl: string;
  uri = '';
  warningResponse = '';

  private fileNameList = {};
  private formData: any;
  private modulePermissions: ProgramModulePermissions;
  private ngUnsubscribe: Subject<any> = new Subject();
  private oesDynamicFormComponent!: DynamicFormComponent;
  private text = {};

  constructor(private _eventService: EventService,
              private _programApplicationService: ProgramApplicationService,
              private _dynamicFormService: DynamicFormService,
              private _programService: ProgramService,
              private _translateService: TranslateService,
              private _userService: UserService,
              private _utilityService: UtilityService,
              private _sideNavigationSecondaryService: SideNavigationSecondaryService,
              private _documentDataService: DocumentDataService,
              private _programFormService: ProgramFormService,
              private _programPageStatusService: ProgramPageStatusService,
              private _programApplicationFormService: ProgramApplicationFormService) {
    this._translateService.get('program.pre-qualification')
      .pipe(take(1))
      .subscribe(response => {
        this.text = response;
    });
  }

  ngOnInit() {
    this.loadingForm = true;
    this.currentRole = this._userService.getSelectedRole();
    this.loadProgram();
    this.fileNameList = getFormFilesList();
  }

  canDeactivate(): boolean {
    return !this._programPageStatusService.isDirty;
  }

  public ngOnDestroy() {
    this._programPageStatusService.reset();
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  private loadProgram() {
    this._programService.getProgram(this._programService.programId)
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe((program: Program) => {
      this.program = program;
      this.modulePermissions = program.programPermissions.modulePermissions;
      this.getProgramApplications(this.program);
    });
  }

  private getProgramApplications(program: Program) {
    if (program?.programApplications?.length) {
      this.programApplication = program.programApplications.find(app => app.programApplicationStageConfiguration?.stageRank === this.stage);
      this.programApplication.program = new Program({ id: this.program.id });

      this.currentStatusSubmitted = this._utilityService.findProgramApplicationStatus(this.programApplication.applicationStatuses) === 'Submitted';
      this._documentDataService.setFileUploadStorageModel(
        this.programApplication.participant.id, FORM_TYPE.PRE_QUALIFICATION, this.programApplication.id);
      this.getApplicationForm();

      this.setReadOnly();
      this.setDataRoomReadOnly();
    }
  }

  private reloadProgramApplication() {
    this._programApplicationService.findProgramApplications(this.program.id)
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe((programApplications: ProgramApplication[]) => {
      if (programApplications && programApplications.length > 0) {
        this.programApplication = programApplications[0];
        this._documentDataService.setFileUploadStorageModel(
          this.programApplication.participant.id, FORM_TYPE.PRE_QUALIFICATION, this.programApplication.id);
      }
    });
  }

  public triggerSave() {
    this.oesDynamicFormComponent.saveTriggered();
  }

  // For Pre-qualification
  private getApplicationForm() {
    switch (this._programService.getProgramType(this.program).enum) {
      case PROGRAM_TYPE.PUBLIC_PRE_QUALIFICATION:
      case PROGRAM_TYPE.INVITE_ONLY_PRE_QUALIFICATION:
        this.json = undefined;
        this._programApplicationFormService.getForm(
          this.program.id,
          this.programApplication.id,
          FORM_ID.PRE_QUALIFICATION
        )
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(prequalFormJson => {
          const testing = Object.keys(prequalFormJson);
          if (testing && testing.length > 0) {
            this.json = this._dynamicFormService.removeSaveButtons(prequalFormJson);
            this.loadingForm = false;
          } else {
            this.getProgramForm();
          }
          // set tagTypes
          this.tagTypes = [{
            docTagItemId: this.programApplication.id,
            docTagType: DOCUMENT_TAG_TYPE.PRE_QUALIFICATION,
            name: (DOCUMENT_TAG_TYPE.PRE_QUALIFICATION).replace('PREQ', 'PRE-Q').toLowerCase()
          }, {
            docTagItemId: this.program.id,
            docTagType: DOCUMENT_TAG_TYPE.PROGRAM,
            name: this.program.name
          }];
        });
        break;
    }
  }

  private getProgramForm() {
    this._programFormService.getForm(this.program.id, FORM_ID.PRE_QUALIFICATION)
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(prequalFormJson => {
      const testing = Object.keys(prequalFormJson);
      if (testing?.length > 0) {
        this.json = this._dynamicFormService.removeSaveButtons(prequalFormJson);
      }
      this.loadingForm = false;
    });
  }

  public isDirty(): boolean {
    return this._programPageStatusService.isDirty;
  }

  /**
   * callback oes-dynamic-form
   * Insert responses into json and save it to Program:Documents
   */
  public formSave(event: any) {
    this._programApplicationService.saveApplication(
      this.program.id,
      this.fileNameList['preQualification'],
      this._dynamicFormService.updateDefaultValue(this.json, event),
      this.programApplication.id
    )
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(savedFormData => {
      if (savedFormData) {
        this._programPageStatusService.isDirty = false;
        this._eventService.success(this.text['saved']);
      }
    });
  }

  // callback oes-dynamic-form
  public formChanged(event: any) {
    this._programPageStatusService.isDirty = true;
  }

  /**
   * callback oes-dynamic-form
   * Save form response status to the back-end
   */
  public beforeSubmit(event) {
    if (event === undefined) {
      this.formData = this.oesDynamicFormComponent.updatedForm;
    } else {
      this.formData = event;
    }
    const timeMs = 150;
    this.scrollToTop(timeMs, '.drawer');
    setTimeout(() => {
      this.modalDialogComponent.show();
    }, timeMs);
  }

  scrollToTop(duration: number, elementName: string) {
    const element = document.querySelector(elementName);
    if (!element) {
      return;
    }

    const start = element.scrollTop;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      element.scrollTop = start * (1 - this.easeInOutQuad(progress));

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  // TODO: merge this with formSave()
  // callback modal
  public submitProgramApplication() {
    if (!this.formValid) {
      return;
    }
    this.modalDialogComponent.hide();
    // save
    this._programApplicationService.saveApplication(
      this.program.id,
      this.fileNameList['preQualification'],
      this._dynamicFormService.updateDefaultValue(this.json, this.formData),
      this.programApplication.id
    )
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(savedFormData => {
      // submit
      this._programApplicationService.updateProgramApplicationStatus(this.programApplication, 'submit')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(programApplication => {
        this._programPageStatusService.isDirty = false;
        // reload
        this._sideNavigationSecondaryService.reload = true;
        this.loadProgram();
        this._eventService.success(this._translateService.instant('program.pre-qualification.submission-success'));
      });
    });
  }

  private setReadOnly(): void {
    if (this.programApplication && (this.modulePermissions.application.find(app => app.stageConfigId === this.programApplication.programApplicationStageConfiguration.id).application.readOnly || this.isExpired())) {
      this.readOnly = true;
    } else {
      this.readOnly = false;
    }
  }

  private setDataRoomReadOnly(): void {
    if (this.programApplication && (this.modulePermissions.application.find(app => app.stageConfigId === this.programApplication.programApplicationStageConfiguration.id).applicationDocuments.readOnly || this.isExpired())) {
      this.dataRoomReadOnly = true;
    } else {
      this.dataRoomReadOnly = false;
    }
  }

  private isExpired(): boolean {
    if (this.programApplication && this.programApplication.program) {
      const endDate = new Date(this.programApplication.program.preQualEndDateTime).getTime();
      const now = new Date().getTime();
      return now > endDate;
    }
    return false;
  }

  get stageConfigName(): string | undefined {
    return this.program?.programApplicationStageConfigurations?.find(c => c.stageRank === this.stage)?.name;
  }

  get accessControl (): any {
    if (!this.dataRoomReadOnly) {
      return new FilesActions({
        copy: true,
        create: true,
        delete: true,
        download: true,
        move: true,
        upload: true,
        tag: true,
        editName: true,
        preview: true
      });
    } else {
      return new FilesActions({
        download: true,
        preview: true
      });
    }
  }
}
